import {
  WESTERN_PLANETS,
  type AyanamsaId,
  type ChartFact,
  RASHI_NAMES,
  type ChartPointId,
  type ChartResult,
  type GocharPlacement,
  type Planet,
  type PlanetPosition,
  type TransitHit,
  type WesternAspectType,
} from '@astrally/shared';
import type { EphemerisAdapter } from '../ephemeris/adapter.js';
import { getDefaultAdapter } from '../ephemeris/index.js';
import { computePositions } from '../core/positions.js';
import { signCount } from '../core/zodiac.js';
import { angleDiff, formatDegMin, norm360 } from '../math/angles.js';
import { ASPECT_ANGLES, type ChartPoint } from '../western/aspects.js';
import { computeGochar, computeTransits } from '../western/transits.js';
import { DASHA_SEQUENCE, activePeriods } from '../jyotish/vimshottari.js';
import { computeHinduDay, computePanchanga, type Panchanga } from '../jyotish/panchanga.js';
import { buildChartFacts, buildTransitFacts } from '../chart/facts.js';
import {
  AMAVASYA_THEME,
  DASHA_LEVEL_FRAMES,
  DASHA_LORD_THEMES,
  GOCHARA_WEIGHTS,
  HOROSCOPE_DISCLAIMER,
  LIFE_AREAS,
  MOON_HOUSE_THEMES,
  MOON_PHASE_ORDER,
  MOON_PHASE_THEMES,
  NAKSHATRA_THEMES,
  NATAL_POINT_WEIGHTS,
  PERIOD_FRAMES,
  PERIOD_TUNING,
  PER_DAY_LIMB_SCALE,
  PURNIMA_THEME,
  SADE_SATI_PHASES,
  SADE_SATI_PHASE_NAMES,
  TITHI_GROUP_ORDER,
  TITHI_GROUP_THEMES,
  VARA_THEMES,
  VEDIC_PERIOD_BOOST,
  WESTERN_PERIOD_BOOST,
  aspectTheme,
  grahaLabel,
  isDashaLord,
  karanaTheme,
  nityaYogaTheme,
  type HoroscopePeriod,
  type LifeArea,
  type MoonPhase,
  type Theme,
} from './themes.js';

/**
 * Deterministic period readings ("horoscopes") for both traditions.
 *
 * This is the substance layer. The AI narration step later rephrases what
 * comes out of here — it never adds astrology. Two users with the same chart,
 * the same window and the same system get byte-identical output, which is what
 * makes the accuracy-tracking feature scoreable at all.
 *
 * Purity contract:
 *  - No Date.now(), no Math.random(), no locale- or timezone-dependent
 *    formatting. Every instant used is derived from `fromUtc`/`toUtc`.
 *  - Every Signal carries the engine fact ids it was derived from, and every
 *    one of those ids resolves inside the returned `facts` pack (whose id
 *    vocabulary is owned by src/chart/facts.ts).
 *
 * Reference points differ by tradition on purpose:
 *  - VEDIC reads gochara from the sign the Moon occupied at birth (the chandra
 *    lagna), in the sidereal zodiac, plus the running Vimshottari dasha and —
 *    for a day — the five panchanga limbs.
 *  - WESTERN reads transits to the whole natal chart in the tropical zodiac,
 *    weighted by how slowly the transiting body is actually moving.
 */

export type FactId = string;
export type HoroscopeSystem = 'vedic' | 'western';
export type { HoroscopePeriod, LifeArea } from './themes.js';

export interface Signal {
  /** Stable, deterministic identity. Also the de-duplication key. */
  id: string;
  label: string;
  meaning: string;
  /** 0..1. Higher means the engine considers it more significant this period. */
  weight: number;
  area: LifeArea;
  /** When an aspect perfects inside the window, if it does. */
  exactAtUtc?: string;
  /** Engine fact ids this reading is traceable to. Never empty. */
  factIds: FactId[];
  /** Glossary term id, when the label contains jargon worth explaining. */
  term?: string;
}

export interface SadeSatiFlag {
  phase: 'rising' | 'peak' | 'setting';
  /** 12, 1 or 2 — Saturn's house counted from the natal Moon sign. */
  houseFromMoon: number;
  signalId: string;
}

export interface ComposeHoroscopeInput {
  chart: ChartResult;
  /** Sidereal sign index (0 = Aries) the Moon occupied at birth. */
  natalMoonSign: number;
  period: HoroscopePeriod;
  fromUtc: Date;
  toUtc: Date;
  system: HoroscopeSystem;
  /** Where the reader is now — used for the sunrise that anchors a panchanga. */
  lat: number;
  lon: number;
  adapter?: EphemerisAdapter;
  /** Defaults to the ayanamsa the chart was calculated with. */
  ayanamsaId?: AyanamsaId;
}

export interface HoroscopeResult {
  system: HoroscopeSystem;
  period: HoroscopePeriod;
  fromUtcISO: string;
  toUtcISO: string;
  /**
   * The instant the fact pack describes: local sunrise for a day reading,
   * the midpoint of the window otherwise.
   */
  referenceUtcISO: string;
  headline: string;
  /**
   * Sidereal sign index (0 = Mesha) of the natal Moon — the rashi this
   * reading is counted from. Present for BOTH systems (the western composer
   * receives the same natal anchor), but only the Jyotish UI leads with it.
   */
  anchorRashi: number;
  /** The highest-weight signals, already sorted. The UI surface. */
  strongest: Signal[];
  /** Every retained signal, bucketed. Keys always present, values may be empty. */
  byArea: Record<LifeArea, Signal[]>;
  /** Union of every signal's fact ids, sorted. */
  sources: FactId[];
  /** The resolved facts for `sources`, same order. The AI's grounding pack. */
  facts: ChartFact[];
  /** Vedic only. Present when Saturn transits the 12th/1st/2nd from the natal Moon. */
  sadeSati?: SadeSatiFlag;
  /** Must accompany any narration of this reading. */
  disclaimer: string;
}

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

const HOUR_MS = 3600000;
const DAY_MS = 24 * HOUR_MS;

/** Sampling cadence per period. Fine enough to catch an aspect perfecting. */
const SAMPLE_STEP_MS: Record<HoroscopePeriod, number> = {
  day: 4 * HOUR_MS,
  week: 4 * HOUR_MS,
  month: DAY_MS,
  year: 5 * DAY_MS,
};

/**
 * buildTransitFacts caps each call at fifteen transits. Feeding it in chunks
 * mints a fact for every configuration a Signal cites, while keeping facts.ts
 * as the single owner of the id template.
 */
const TRANSIT_FACT_CHUNK = 15;

function sampleInstants(fromUtc: Date, toUtc: Date, stepMs: number): Date[] {
  const start = fromUtc.getTime();
  const end = Math.max(start, toUtc.getTime());
  const out: Date[] = [];
  for (let t = start; t < end; t += stepMs) out.push(new Date(t));
  if (out.length === 0 || out[out.length - 1].getTime() !== end) out.push(new Date(end));
  return out;
}

/** Day boundaries inside the window, so a week reading is literally its days. */
function dayStarts(fromUtc: Date, toUtc: Date): Date[] {
  const start = fromUtc.getTime();
  const end = Math.max(start, toUtc.getTime());
  const out: Date[] = [];
  for (let t = start; t < end || out.length === 0; t += DAY_MS) out.push(new Date(t));
  return out;
}

// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function round3(x: number): number {
  return Math.round(x * 1000) / 1000;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Read from DASHA_SEQUENCE so the copy can never disagree with the maths. */
function mahadashaYears(lord: Planet): number {
  return DASHA_SEQUENCE.find((d) => d.lord === lord)?.years ?? 0;
}

/** Slower planet, heavier transit. 13°/day Moon ≈ 0.07; 0.03°/day Saturn ≈ 0.97. */
function speedFactor(degreesPerDay: number): number {
  return 1 / (1 + Math.abs(degreesPerDay));
}

/** Tight orb, loud signal. 0° → 1.0, 3° → 0.4. */
function orbFactor(orb: number): number {
  return 1 - (0.6 * Math.min(Math.abs(orb), 3)) / 3;
}

function boostFor(
  system: HoroscopeSystem,
  period: HoroscopePeriod,
  planet: Planet,
): number {
  const table = system === 'western' ? WESTERN_PERIOD_BOOST : VEDIC_PERIOD_BOOST;
  const value = table[period][planet];
  return value === undefined ? 1 : value;
}

/** Sort is total: weight desc, then id asc. Never depends on insertion order. */
function sortSignals(signals: Signal[]): Signal[] {
  return [...signals].sort((a, b) => (b.weight - a.weight) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

function mergeSignals(signals: Signal[]): Signal[] {
  const byId = new Map<string, Signal>();
  for (const s of signals) {
    const existing = byId.get(s.id);
    if (!existing) {
      byId.set(s.id, { ...s, factIds: [...s.factIds] });
      continue;
    }
    const factIds = Array.from(new Set([...existing.factIds, ...s.factIds])).sort();
    const exactAtUtc =
      existing.exactAtUtc && s.exactAtUtc
        ? existing.exactAtUtc < s.exactAtUtc
          ? existing.exactAtUtc
          : s.exactAtUtc
        : (existing.exactAtUtc ?? s.exactAtUtc);
    const winner = s.weight > existing.weight ? s : existing;
    byId.set(s.id, { ...winner, factIds, ...(exactAtUtc ? { exactAtUtc } : {}) });
  }
  return sortSignals(Array.from(byId.values()));
}

// ---------------------------------------------------------------------------
// Natal side
// ---------------------------------------------------------------------------

/**
 * The natal points a Western transit can hit.
 *
 * For window-mode charts (approximate / unknown birth time) there is no exact
 * position list. We fall back to the window midpoint, but ONLY for bodies that
 * `scanWindow` already proved stable across the window, and never for the Moon
 * or the angles — those are exactly the things an unknown birth time destroys.
 */
function natalChartPoints(chart: ChartResult): ChartPoint[] {
  const pts: ChartPoint[] = [];
  if (chart.positions) {
    for (const p of chart.positions) {
      if (WESTERN_PLANETS.includes(p.planet)) pts.push({ id: p.planet, lon: p.lonTropical });
    }
    if (chart.western?.angles) {
      pts.push({ id: 'ascendant', lon: chart.western.angles.ascendant });
      pts.push({ id: 'mc', lon: chart.western.angles.mc });
    }
    if (chart.western?.fortune) {
      pts.push({ id: 'fortune', lon: chart.western.fortune.lon });
    }
    return pts;
  }

  if (chart.uncertainty) {
    const stable = new Set(
      chart.uncertainty.stable
        .filter((f) => f.id.startsWith('planet.') && f.id.endsWith('.siderealSign'))
        .map((f) => f.id.split('.')[1]),
    );
    for (const p of chart.uncertainty.midpointPositions) {
      if (p.planet === 'moon') continue;
      if (!WESTERN_PLANETS.includes(p.planet)) continue;
      if (!stable.has(p.planet)) continue;
      pts.push({ id: p.planet, lon: p.lonTropical });
    }
  }
  return pts;
}

function natalFactId(point: ChartPointId): string | undefined {
  if (point === 'ascendant') return 'western.ascendant';
  if (point === 'mc') return 'western.mc';
  if (point === 'fortune') return undefined;
  return `western.${point}.position`;
}

// ---------------------------------------------------------------------------
// Day anchoring
// ---------------------------------------------------------------------------

/**
 * The sunrise that opens the Hindu day inside this window.
 *
 * A Vedic day runs sunrise to sunrise, so a reading for a UTC-bounded day
 * anchors on the first sunrise it contains. Falls back to the window start
 * when the window contains no sunrise (a sub-day window, or a polar case).
 */
function firstSunriseIn(
  fromUtc: Date,
  toUtc: Date,
  lat: number,
  lon: number,
  adapter: EphemerisAdapter,
): Date {
  const day = computeHinduDay(fromUtc, lat, lon, adapter);
  for (const candidate of [day.sunriseUtc, day.nextSunriseUtc]) {
    if (candidate.getTime() >= fromUtc.getTime() && candidate.getTime() <= toUtc.getTime()) {
      return candidate;
    }
  }
  return fromUtc;
}

/** The Sun–Moon angle is zodiac-independent, so this is the same number either way. */
export function moonPhaseOf(positions: PlanetPosition[]): MoonPhase {
  const sun = positions.find((p) => p.planet === 'sun');
  const moon = positions.find((p) => p.planet === 'moon');
  if (!sun || !moon) throw new Error('Moon phase needs the Sun and the Moon');
  const angle = norm360(moon.lonTropical - sun.lonTropical);
  return MOON_PHASE_ORDER[Math.floor(norm360(angle + 22.5) / 45) % 8];
}

// ---------------------------------------------------------------------------
// Western transit tracking
// ---------------------------------------------------------------------------

interface TransitTrack {
  transiting: Planet;
  natal: ChartPointId;
  type: WesternAspectType;
  natalLon: number;
  tightestOrb: number;
  speedAtTightest: number;
  sampleIndexAtTightest: number;
}

function trackKey(t: Planet, type: WesternAspectType, n: ChartPointId): string {
  return `${t}|${type}|${n}`;
}

/**
 * When does the aspect perfect? Linear root-find on the signed angular error
 * between the transiting body and the exact aspect degree, across the sample
 * grid. Returns undefined when it never crosses inside the window.
 */
function exactTimeFor(
  track: TransitTrack,
  samples: Date[],
  series: number[],
): string | undefined {
  const targetAngle = ASPECT_ANGLES[track.type];
  const branchA = norm360(track.natalLon + targetAngle);
  const branchB = norm360(track.natalLon - targetAngle);
  const at = track.sampleIndexAtTightest;
  const branch =
    Math.abs(angleDiff(series[at], branchA)) <= Math.abs(angleDiff(series[at], branchB))
      ? branchA
      : branchB;

  for (let i = 0; i + 1 < samples.length; i++) {
    const e0 = angleDiff(series[i], branch);
    const e1 = angleDiff(series[i + 1], branch);
    if (e0 === 0) return samples[i].toISOString();
    if (e0 > 0 === e1 > 0) continue;
    // Guard against the ±180 wrap being mistaken for a crossing.
    if (Math.abs(e0) + Math.abs(e1) > 90) continue;
    const frac = Math.abs(e0) / (Math.abs(e0) + Math.abs(e1));
    const t = samples[i].getTime() + frac * (samples[i + 1].getTime() - samples[i].getTime());
    return new Date(Math.round(t)).toISOString();
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Fact pack
// ---------------------------------------------------------------------------

interface GocharSpan {
  house: number;
  signName: string;
  degInSign: number;
  nakshatraName: string;
  retrograde: boolean;
}

/**
 * Facts are built once, at the top level, from src/chart/facts.ts. Two values
 * are period-scoped rather than instant-scoped and are stated as such:
 *  - `transit.*` describes the tightest the configuration gets in the window.
 *  - `gochar.*` lists every house-from-Moon the graha occupies in the window
 *    when it changes, so a signal from day 5 of a week is not cited against
 *    day 1's placement.
 */
function buildFactIndex(
  chart: ChartResult,
  gocharAtReference: GocharPlacement[],
  gocharSpans: Map<Planet, GocharSpan[]>,
  transits: TransitHit[],
  dashaAtUtc: Date,
): Map<string, ChartFact> {
  const index = new Map<string, ChartFact>();
  for (const f of buildChartFacts(chart)) index.set(f.id, f);
  for (let i = 0; i < Math.max(1, transits.length); i += TRANSIT_FACT_CHUNK) {
    for (const f of buildTransitFacts(gocharAtReference, transits.slice(i, i + TRANSIT_FACT_CHUNK))) {
      index.set(f.id, f);
    }
  }

  // Widen gochar facts that moved during the window.
  for (const [planet, spans] of gocharSpans) {
    if (spans.length < 2) continue;
    const existing = index.get(`gochar.${planet}`);
    if (!existing) continue;
    index.set(`gochar.${planet}`, {
      ...existing,
      value: spans
        .map(
          (s) =>
            `${s.signName} ${formatDegMin(s.degInSign)}, ${s.nakshatraName}, house ${s.house} from your Moon${s.retrograde ? ', retrograde' : ''}`,
        )
        .join(' → '),
    });
  }

  // buildChartFacts resolves the dasha against the wall clock. Recompute it
  // against the period being read, so this function stays pure.
  if (chart.jyotish?.vimshottari) {
    const chain = activePeriods(chart.jyotish.vimshottari, dashaAtUtc);
    const confidence = chart.meta.timeConfidence === 'exact' ? 'exact' : 'range';
    if (chain.length === 0) {
      index.delete('jyotish.dasha.current');
      index.delete('jyotish.dasha.mahadashaSpan');
    } else {
      index.set('jyotish.dasha.current', {
        id: 'jyotish.dasha.current',
        label: 'Current dasha',
        value: chain
          .map((p) => `${cap(p.lord)} ${['maha', 'antar', 'pratyantar'][p.level - 1]}dasha`)
          .join(' → '),
        system: 'jyotish',
        confidence,
      });
      const maha = chain[0];
      index.set('jyotish.dasha.mahadashaSpan', {
        id: 'jyotish.dasha.mahadashaSpan',
        label: `${cap(maha.lord)} mahadasha period`,
        value: `${maha.startISO.slice(0, 10)} to ${maha.endISO.slice(0, 10)}`,
        system: 'jyotish',
        confidence,
      });
    }
  }

  return index;
}

// ---------------------------------------------------------------------------
// Signal building
// ---------------------------------------------------------------------------

interface DraftSignal {
  id: string;
  theme: Theme;
  weight: number;
  factIds: string[];
  exactAtUtc?: string;
  labelOverride?: string;
}

function toSignal(draft: DraftSignal, index: Map<string, ChartFact>): Signal | null {
  const resolved = draft.factIds.filter((id) => index.has(id));
  // The first id is the primary provenance. No primary, no signal — a reading
  // that cannot be traced back to engine output does not get published.
  if (resolved.length === 0 || resolved[0] !== draft.factIds[0]) return null;
  return {
    id: draft.id,
    label: draft.labelOverride ?? draft.theme.label,
    meaning: draft.theme.meaning,
    weight: round3(clamp01(draft.weight)),
    area: draft.theme.area,
    factIds: resolved,
    ...(draft.exactAtUtc ? { exactAtUtc: draft.exactAtUtc } : {}),
    ...(draft.theme.term ? { term: draft.theme.term } : {}),
  };
}

// ---------------------------------------------------------------------------
// The composer
// ---------------------------------------------------------------------------

export function composeHoroscope(input: ComposeHoroscopeInput): HoroscopeResult {
  const { chart, natalMoonSign, period, system, lat, lon } = input;
  const adapter = input.adapter ?? getDefaultAdapter();
  const ayanamsaId = input.ayanamsaId ?? chart.meta.ayanamsa.id;

  const fromUtc = new Date(input.fromUtc.getTime());
  const toUtc = new Date(Math.max(input.fromUtc.getTime(), input.toUtc.getTime()));
  const midUtc = new Date(Math.round((fromUtc.getTime() + toUtc.getTime()) / 2));

  const samples = sampleInstants(fromUtc, toUtc, SAMPLE_STEP_MS[period]);
  const samplePositions = samples.map((t) => computePositions(t, adapter, ayanamsaId));

  // The instant the fact pack describes. A day reading anchors on local
  // sunrise, which is where a Vedic day legitimately starts.
  const referenceUtc =
    period === 'day' ? firstSunriseIn(fromUtc, toUtc, lat, lon, adapter) : midUtc;
  const referencePositions = computePositions(referenceUtc, adapter, ayanamsaId);
  const gocharAtReference = computeGochar(
    { at: referenceUtc, positions: referencePositions },
    natalMoonSign,
  );

  // Every distinct house-from-Moon each graha occupies inside the window.
  const gocharSpans = new Map<Planet, GocharSpan[]>();
  for (let i = 0; i < samples.length; i++) {
    for (const g of computeGochar({ at: samples[i], positions: samplePositions[i] }, natalMoonSign)) {
      const house = g.houseFromMoon ?? signCount(natalMoonSign, g.sidereal.sign);
      const spans = gocharSpans.get(g.planet) ?? [];
      if (spans.length === 0 || spans[spans.length - 1].house !== house) {
        spans.push({
          house,
          signName: g.sidereal.signName,
          degInSign: g.sidereal.degInSign,
          nakshatraName: g.nakshatra.name,
          retrograde: g.retrograde,
        });
        gocharSpans.set(g.planet, spans);
      }
    }
  }

  // Transits are tracked once and reused for both the signals and the fact
  // pack, so a Signal's `transit.*` id can never disagree with the fact it cites.
  const tracks =
    system === 'western' ? westernTracks(chart, samples, samplePositions) : new Map<string, TransitTrack>();

  const drafts: DraftSignal[] =
    system === 'western'
      ? westernDrafts({ period, samples, samplePositions, tracks })
      : vedicDrafts({
          chart,
          period,
          samples,
          gocharSpans,
          midUtc,
          lon,
          adapter,
          ayanamsaId,
          lat,
        });

  const transitsForFacts = transitFactsFor(drafts, tracks);
  const index = buildFactIndex(chart, gocharAtReference, gocharSpans, transitsForFacts, midUtc);

  const tuning = PERIOD_TUNING[period];
  const signals = mergeSignals(
    drafts
      .map((d) => toSignal(d, index))
      .filter((s): s is Signal => s !== null)
      .filter((s) => s.weight >= tuning.minWeight),
  ).slice(0, tuning.maxSignals);

  const byArea = {} as Record<LifeArea, Signal[]>;
  for (const area of LIFE_AREAS) byArea[area] = [];
  for (const s of signals) byArea[s.area].push(s);

  const sources = Array.from(new Set(signals.flatMap((s) => s.factIds))).sort();
  const facts = sources.map((id) => index.get(id)).filter((f): f is ChartFact => f !== undefined);

  const sadeSatiSignal = signals.find((s) => s.id.startsWith('vedic.sadeSati.'));
  const sadeSatiHouse = sadeSatiSignal ? Number(sadeSatiSignal.id.split('.')[2]) : undefined;

  return {
    system,
    period,
    fromUtcISO: fromUtc.toISOString(),
    toUtcISO: toUtc.toISOString(),
    referenceUtcISO: referenceUtc.toISOString(),
    headline: headlineFor(period, system, signals, natalMoonSign),
    // The UI renders the reader's rashi as its own tappable element, not by
    // re-parsing the headline — so the index travels alongside it.
    anchorRashi: ((natalMoonSign % 12) + 12) % 12,
    strongest: signals.slice(0, tuning.maxStrongest),
    byArea,
    sources,
    facts,
    ...(sadeSatiSignal && sadeSatiHouse !== undefined
      ? {
          sadeSati: {
            phase: SADE_SATI_PHASE_NAMES[sadeSatiHouse],
            houseFromMoon: sadeSatiHouse,
            signalId: sadeSatiSignal.id,
          },
        }
      : {}),
    disclaimer: HOROSCOPE_DISCLAIMER,
  };
}

/**
 * "Today, Vedic lens: …" was writer's-workshop vocabulary in the single most
 * read sentence of the product, and it never named the reader. A Jyotish
 * reading is a RASHIFAL and it belongs to a rashi — so the headline now leads
 * with the reader's own Chandra rashi by name ("Aaj ka rashifal — Kanya: …"),
 * which is both the plain Indian convention and the thing the founder asked
 * for verbatim: "Where are the Rashis — Dhanu, etc". The word "lens" is
 * banned from output copy repo-wide; see the style note in themes.ts.
 */
function headlineFor(
  period: HoroscopePeriod,
  system: HoroscopeSystem,
  signals: Signal[],
  natalMoonSign: number,
): string {
  const frame = PERIOD_FRAMES[period];
  const quiet = 'a quiet sky. Nothing is shouting, which is its own kind of information';
  if (system === 'vedic') {
    const rashi = RASHI_NAMES[((natalMoonSign % 12) + 12) % 12];
    const opening =
      period === 'day' ? `Aaj ka rashifal — ${rashi}` : `${frame.word} for ${rashi} rashi`;
    return `${opening}: ${signals.length === 0 ? quiet : signals[0].label}.`;
  }
  return `${frame.word} in your Western chart: ${signals.length === 0 ? quiet : signals[0].label}.`;
}

// ---------------------------------------------------------------------------
// Western
// ---------------------------------------------------------------------------

interface WesternArgs {
  period: HoroscopePeriod;
  samples: Date[];
  samplePositions: PlanetPosition[][];
  tracks: Map<string, TransitTrack>;
}

function westernTracks(
  chart: ChartResult,
  samples: Date[],
  samplePositions: PlanetPosition[][],
): Map<string, TransitTrack> {
  const natalPoints = natalChartPoints(chart);
  const natalLonById = new Map<ChartPointId, number>(natalPoints.map((p) => [p.id, p.lon]));
  const tracks = new Map<string, TransitTrack>();
  if (natalPoints.length === 0) return tracks;

  for (let i = 0; i < samples.length; i++) {
    const positions = samplePositions[i];
    const hits = computeTransits(natalPoints, { at: samples[i], positions });
    for (const hit of hits) {
      const key = trackKey(hit.transiting, hit.type, hit.natal);
      const existing = tracks.get(key);
      if (existing && existing.tightestOrb <= hit.orb) continue;
      const moving = positions.find((p) => p.planet === hit.transiting);
      tracks.set(key, {
        transiting: hit.transiting,
        natal: hit.natal,
        type: hit.type,
        natalLon: natalLonById.get(hit.natal) ?? 0,
        tightestOrb: hit.orb,
        speedAtTightest: moving ? moving.speed : 0,
        sampleIndexAtTightest: i,
      });
    }
  }
  return tracks;
}

function westernDrafts(args: WesternArgs): DraftSignal[] {
  const { period, samples, samplePositions, tracks } = args;
  const drafts: DraftSignal[] = [];

  for (const track of Array.from(tracks.values()).sort((a, b) =>
    trackKey(a.transiting, a.type, a.natal) < trackKey(b.transiting, b.type, b.natal) ? -1 : 1,
  )) {
    const boost = boostFor('western', period, track.transiting);
    if (boost === 0) continue;
    const theme = aspectTheme(track.transiting, track.type, track.natal);
    const weight =
      speedFactor(track.speedAtTightest) *
      theme.weight *
      orbFactor(track.tightestOrb) *
      NATAL_POINT_WEIGHTS[track.natal] *
      boost;

    const series = samplePositions.map((positions) => {
      const p = positions.find((q) => q.planet === track.transiting);
      return p ? p.lonTropical : 0;
    });

    const natalId = natalFactId(track.natal);
    drafts.push({
      id: `western.transit.${track.transiting}-${track.type}-${track.natal}`,
      theme,
      weight,
      factIds: [
        `transit.${track.transiting}-${track.type}-${track.natal}`,
        ...(natalId ? [natalId] : []),
      ],
      exactAtUtc: exactTimeFor(track, samples, series),
    });
  }

  // The lunar phase is the legible daily driver on the Western side. It is the
  // Sun–Moon angle, which is the same number in either zodiac.
  const limbScale = PER_DAY_LIMB_SCALE[period];
  if (limbScale > 0) {
    for (const dayStart of dayStarts(samples[0], samples[samples.length - 1])) {
      const phase = moonPhaseOf(samplePositions[nearestSampleIndex(samples, dayStart)]);
      drafts.push({
        id: `western.moonPhase.${phase}`,
        theme: MOON_PHASE_THEMES[phase],
        weight: MOON_PHASE_THEMES[phase].weight * limbScale,
        factIds: ['gochar.moon', 'gochar.sun'],
      });
    }
  }

  return drafts;
}

function nearestSampleIndex(samples: Date[], at: Date): number {
  let best = 0;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (let i = 0; i < samples.length; i++) {
    const delta = Math.abs(samples[i].getTime() - at.getTime());
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  }
  return best;
}

/**
 * The TransitHit list handed to buildTransitFacts: exactly the configurations a
 * draft cited, heaviest first, so every `transit.*` citation resolves.
 */
function transitFactsFor(
  drafts: DraftSignal[],
  tracks: Map<string, TransitTrack>,
): TransitHit[] {
  const weightByKey = new Map<string, number>();
  for (const d of drafts) {
    const primary = d.factIds[0];
    if (!primary?.startsWith('transit.')) continue;
    const key = primary.slice('transit.'.length);
    weightByKey.set(key, Math.max(weightByKey.get(key) ?? 0, d.weight));
  }
  if (weightByKey.size === 0) return [];
  const scored: { hit: TransitHit; score: number }[] = [];
  for (const track of tracks.values()) {
    const score = weightByKey.get(`${track.transiting}-${track.type}-${track.natal}`);
    if (score === undefined) continue;
    scored.push({
      hit: {
        transiting: track.transiting,
        natal: track.natal,
        type: track.type,
        orb: track.tightestOrb,
      },
      score,
    });
  }
  return scored.sort((a, b) => b.score - a.score || a.hit.orb - b.hit.orb).map((s) => s.hit);
}

// ---------------------------------------------------------------------------
// Vedic
// ---------------------------------------------------------------------------

interface VedicArgs {
  chart: ChartResult;
  period: HoroscopePeriod;
  samples: Date[];
  gocharSpans: Map<Planet, GocharSpan[]>;
  midUtc: Date;
  lat: number;
  lon: number;
  adapter: EphemerisAdapter;
  ayanamsaId: AyanamsaId;
}

function vedicDrafts(args: VedicArgs): DraftSignal[] {
  const { chart, period, samples, gocharSpans, midUtc, lat, lon, adapter, ayanamsaId } = args;
  const drafts: DraftSignal[] = [];

  // 1. Gochara — every house-from-Moon each graha occupies in the window.
  const planets = Array.from(gocharSpans.keys()).sort();
  for (const planet of planets) {
    const boost = boostFor('vedic', period, planet);
    if (boost === 0) continue;
    const base = GOCHARA_WEIGHTS[planet];
    if (base === undefined) continue;
    for (const span of gocharSpans.get(planet) ?? []) {
      const houseTheme = MOON_HOUSE_THEMES[span.house];
      if (!houseTheme) continue;
      drafts.push({
        id: `vedic.gochara.${planet}.h${span.house}`,
        theme: {
          ...houseTheme,
          label: `${grahaLabel(planet)} ${houseTheme.label}`,
          meaning: `${houseTheme.meaning} Right now that house is being crossed by ${grahaLabel(planet)}.`,
        },
        weight: base * houseTheme.weight * boost,
        factIds: [`gochar.${planet}`, 'jyotish.moon.position'],
      });

      // 2. Sade Sati — Saturn across the 12th, 1st and 2nd from the natal Moon.
      if (planet === 'saturn' && SADE_SATI_PHASES[span.house]) {
        const phase = SADE_SATI_PHASES[span.house];
        drafts.push({
          id: `vedic.sadeSati.${span.house}`,
          theme: phase,
          weight: phase.weight,
          factIds: [`gochar.saturn`, 'jyotish.moon.position'],
        });
      }
    }
  }

  // 3. Dasha — the chapter everything else is happening inside.
  if (chart.jyotish?.vimshottari) {
    const chain = activePeriods(chart.jyotish.vimshottari, midUtc);
    for (const dashaPeriod of chain.slice(0, 2)) {
      if (!isDashaLord(dashaPeriod.lord)) continue;
      const lordTheme = DASHA_LORD_THEMES[dashaPeriod.lord];
      const frame = DASHA_LEVEL_FRAMES[dashaPeriod.level];
      drafts.push({
        id: `vedic.dasha.${dashaPeriod.level}.${dashaPeriod.lord}`,
        theme: {
          ...lordTheme,
          label:
            dashaPeriod.level === 1
              ? lordTheme.label
              : `${cap(dashaPeriod.lord)} ${frame.name} — the nearer-term flavour`,
          meaning:
            dashaPeriod.level === 1
              ? `${lordTheme.meaning} A ${cap(dashaPeriod.lord)} mahadasha runs ${mahadashaYears(dashaPeriod.lord)} years; yours is ${dashaPeriod.startISO.slice(0, 10)} to ${dashaPeriod.endISO.slice(0, 10)}.`
              : `Inside the bigger chapter, the ${cap(dashaPeriod.lord)} ${frame.name} (sub-period) runs ${dashaPeriod.startISO.slice(0, 10)} to ${dashaPeriod.endISO.slice(0, 10)}. ${lordTheme.meaning}`,
          term: frame.term,
        },
        weight: lordTheme.weight * frame.weight,
        factIds:
          dashaPeriod.level === 1
            ? ['jyotish.dasha.current', 'jyotish.dasha.mahadashaSpan']
            : ['jyotish.dasha.current'],
      });
    }
  }

  // 4. Panchanga — the five limbs, per day, for day and week readings.
  //    Delegated to jyotish/panchanga.ts: it anchors the vaara on real local
  //    sunrise rather than on a UTC boundary, which is the whole point.
  const limbScale = PER_DAY_LIMB_SCALE[period];
  if (limbScale > 0) {
    for (const dayStart of dayStarts(samples[0], samples[samples.length - 1])) {
      const anchor = firstSunriseIn(
        dayStart,
        new Date(dayStart.getTime() + DAY_MS),
        lat,
        lon,
        adapter,
      );
      drafts.push(
        ...panchangaDrafts(computePanchanga(anchor, lat, lon, adapter, ayanamsaId), limbScale),
      );
    }
  }

  return drafts;
}

function panchangaDrafts(p: Panchanga, scale: number): DraftSignal[] {
  const drafts: DraftSignal[] = [];

  const vara = VARA_THEMES[p.vaara.index];
  drafts.push({
    id: `vedic.panchanga.vara.${p.vaara.index}`,
    theme: vara,
    weight: vara.weight * scale,
    // The vaara is the solar day, measured sunrise to sunrise.
    factIds: ['gochar.sun'],
  });

  // panchanga.ts numbers tithis 1..30; the themes are keyed 0-based.
  const tithiIndex = p.tithi.index - 1;
  const full = p.tithi.numberInPaksha === 15;
  const tithiTheme = full
    ? p.tithi.paksha === 'shukla'
      ? PURNIMA_THEME
      : AMAVASYA_THEME
    : TITHI_GROUP_THEMES[TITHI_GROUP_ORDER[(p.tithi.numberInPaksha - 1) % 5]];
  drafts.push({
    id: `vedic.panchanga.tithi.${tithiIndex}`,
    theme: {
      ...tithiTheme,
      label: full
        ? `${tithiTheme.label} (${p.tithi.name})`
        : `${tithiTheme.label} (${p.tithi.name}, ${p.tithi.paksha === 'shukla' ? 'waxing' : 'waning'} day ${p.tithi.numberInPaksha})`,
    },
    weight: tithiTheme.weight * scale,
    factIds: ['gochar.moon', 'gochar.sun'],
  });

  const nak = NAKSHATRA_THEMES[p.nakshatra.index];
  drafts.push({
    id: `vedic.panchanga.nakshatra.${p.nakshatra.index}`,
    theme: nak,
    weight: nak.weight * scale,
    factIds: ['gochar.moon'],
  });

  const yoga = nityaYogaTheme(p.yoga.index);
  drafts.push({
    id: `vedic.panchanga.yoga.${p.yoga.index}`,
    theme: yoga,
    weight: yoga.weight * scale,
    factIds: ['gochar.moon', 'gochar.sun'],
  });

  const karana = karanaTheme(p.karana.name);
  drafts.push({
    id: `vedic.panchanga.karana.${p.karana.name}`,
    theme: karana,
    weight: karana.weight * scale,
    factIds: ['gochar.moon', 'gochar.sun'],
  });

  return drafts;
}
